import { useState, useContext, useEffect, useRef } from 'react'
import { Button } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'

import { tieneMedioPagoHabilitado } from '../../helpers/mediosPago.js'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { useAuth } from '../../hooks/useAuth'
import { useImagenesPublicacion } from '../../hooks/useImagenesPublicacion.js'
import { resetCategorias } from '../../slices/filterSlice'
import { setModal } from '../../slices/modalSlice'
import { CarritoContext } from '../context/carritoContext'
import { calcularTotalItemConIva, calcularTotalItemSinIva } from '../helpers/calcularPrecioTotal'
import ComponenteError from '../publicaciones/ComponenteError'
import { GenericModal } from '../reutilizables/modal/GenericModal'
import Precios from '../reutilizables/precios/Precios'
import * as carritoService from '../servicios/carritoService'
import * as publicacionesService from '../servicios/publicacionesService'
import styles from './Carrito.module.css'
import CarritoItem from './CarritoItem'

export const Carrito = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loginDataContext: { idUsuario, aceptaNoHayDatosFiscales, existeDatosFiscales } = {} } =
    useAuth()
  const {
    setCantidad,
    items,
    setItems,
    setIdPresupuestoComprar,
    setIdPublicacionComprar,
    getImputacion
  } = useContext(CarritoContext)

  const [totalSuma, setTotalSuma] = useState(0)
  const [sumaTotal, setSumaTotal] = useState(0)
  const [itemsSeparados, setItemsSeparados] = useState([])
  const [centroDeCostos, setCentroDeCostos] = useState(null)
  const [tipoConfirmacion, setTipoConfirmacion] = useState(null)
  const [itemAEliminar, setItemAEliminar] = useState(null)

  const { adjuntos, loadingImagenes, fetchImagenes } = useImagenesPublicacion()
  const detalleCompletado = useRef(false)

  const centroDeCostosLlamado = async () => {
    const resp = await getImputacion()
    const opcionesImputTpos = resp.opcionesImputTpos.map((element) => {
      return { value: element.tipoImpu, label: element.tipoImpuTextos }
    })
    const listaImputaciones = resp.listaImputaciones.map((element) => {
      return {
        codigo: element.activoFijo || element.codigo,
        descripcion: element.descripcion,
        tipoImpu: element.tipoImpu
      }
    })
    setCentroDeCostos({
      ...resp,
      opcionesImputTpos,
      listaImputaciones
    })
  }

  useEffect(() => {
    !centroDeCostos && centroDeCostosLlamado()
    sumarTotales()
  }, [totalSuma])

  const sumar = (valor) => {
    setTotalSuma(valor)
  }

  const completarDetallePublicaciones = async () => {
    if (detalleCompletado.current || !items || items.length === 0) {
      return
    }

    const idPublicaciones = items.map((item) => item.idPublicacion)
    const { data } = await publicacionesService.buscarDetallePorIds(idPublicaciones)

    setItems((prevItems) =>
      prevItems.map((item) => {
        const detalle = data.body.find((d) => d.idPublicacion === item.idPublicacion)

        return detalle
          ? {
              ...item,
              mediosPagos: detalle.mediosPagos,
              porcDcto: detalle.porcDcto
            }
          : item
      })
    )

    detalleCompletado.current = true
  }

  useEffect(() => {
    setIdPresupuestoComprar(0)
    setIdPublicacionComprar(0)
  }, [setIdPresupuestoComprar, setIdPublicacionComprar])

  useEffect(() => {
    completarDetallePublicaciones()
  }, [items])

  useEffect(() => {
    const itemsConDescuento = items

    setItemsSeparados(
      Object.values(
        itemsConDescuento.reduce((acc, item) => {
          const tieneDescuento = item.porcDcto > 0

          if (!acc[item.idVendedor]) {
            acc[item.idVendedor] = []
          }
          acc[item.idVendedor].push({
            ...item,
            tieneDescuento,
            valorTotalConIva: calcularTotalItemConIva(item),
            valorTotalSinIva: calcularTotalItemSinIva(item)
          })
          return acc
        }, {})
      )
    )
  }, [items])

  const handleCloseConfirm = () => {
    setTipoConfirmacion(null)
    setItemAEliminar(null)
  }

  const handleConfirmarBorradoItem = (id) => {
    openConfirm('item', id)
  }

  const confirmarBorrado = async () => {
    if (tipoConfirmacion === 'carrito') {
      await deleteCarritoCompleto()
    } else if (tipoConfirmacion === 'item' && itemAEliminar) {
      await deleteCarritoItem(itemAEliminar)
    }
    handleCloseConfirm()
    return true
  }

  const deleteCarritoItem = async (idpub) => {
    try {
      const item = items.find((item) => item.idPublicacion === idpub)

      const carritoEliminar = {
        idPublicacion: idpub,
        idUsuario
      }
      const { data: carritoCantidad } = await carritoService.eliminar(carritoEliminar)
      setSumaTotal(sumaTotal - calcularTotalItemConIva(item))
      const newItems = items.filter((item) => item.idPublicacion !== idpub)
      setItems(newItems)
      setCantidad(carritoCantidad)
    } catch (error) {
      const mensaje = `Ocurrió un error al obtener al eliminar el carrito.`
      toastErrorLog(error, mensaje, 'carrito')
    }
  }

  const deleteCarritoCompleto = async () => {
    try {
      for (const item of items) {
        const carritoEliminar = {
          idPublicacion: item.idPublicacion,
          idUsuario
        }
        await carritoService.eliminar(carritoEliminar)
      }

      setSumaTotal(0)
      setCantidad(0)
      setItems([])
    } catch (error) {
      const mensaje = `Ocurrió un error al eliminar el carrito.`
      toastErrorLog(error, mensaje, 'carrito')
    }
  }

  const sumarTotales = async () => {
    const totalItems = items.map((item) => calcularTotalItemConIva(item))
    const arrTotalesSinIVA = items.map((item) => calcularTotalItemSinIva(item))

    let total = 0
    let totalSinIva = 0
    for (let i = 0; i < totalItems.length; i++) {
      total = total + totalItems[i]
      totalSinIva = totalSinIva + arrTotalesSinIVA[i]
    }
    setSumaTotal(total)
  }

  const modificarCarrito = (valor, propiedad, item, codValor) => {
    const itemsModificados = items.map((i) =>
      i.idPublicacion === item ? { ...i, [propiedad]: valor, [`${propiedad}Cod`]: codValor } : i
    )
    setItems(itemsModificados)
  }

  const copiar = (propiedad, valorAReplicar) => {
    setItems((prev) =>
      prev.map((i) => (i[propiedad] === valorAReplicar ? i : { ...i, [propiedad]: valorAReplicar }))
    )
  }

  const irAPublicacionesFiltrandoPorVendedor = (grupo) => {
    const { idVendedor, razonSocial } = grupo[0]
    dispatch(resetCategorias())
    navigate('/Publicaciones', {
      state: { idVendedorCarrito: idVendedor, razonSocial }
    })
  }

  const openConfirm = (tipo, idItem = null) => {
    setTipoConfirmacion(tipo)
    setItemAEliminar(idItem)

    const body =
      tipo === 'carrito'
        ? '¿Estás seguro de que querés eliminar todo el carrito?'
        : '¿Estás seguro de que querés eliminar esta publicación del carrito?'

    dispatch(
      setModal({
        size: 'sm',
        title: 'Confirmar eliminación',
        type: 'botones',
        body: body,
        show: true
      })
    )
  }
  const hayItemsSinStock = items.some(
    (item) => item.cantDisponible !== null && item.cantDisponible < item.cantidad && !item.aPedido
  )
  const hayItemsComprables = items.some(tieneMedioPagoHabilitado)

  useEffect(() => {
    if (items.length > 0) {
      fetchImagenes({
        idPublicaciones: items.map((item) => item.idPublicacion)
      })
    }
  }, [items])

  return (
    <>
      <div className="pageCard pageCard--conMargenInferior">
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Mi Carrito</h1>
            <p className={styles.subtitle}>
              {items.length > 0
                ? `${items.length} publicación${items.length > 1 ? 'es' : ''} en tu carrito`
                : 'Todavía no agregaste publicaciones'}
            </p>
          </div>
          <div className={styles.headerAcciones}>
            <Link to="/Publicaciones/" className={styles.linkVerMas}>
              Ver más publicaciones
            </Link>
            {items.length > 0 && (
              <Button
                variant="light"
                className={styles.btnVaciar}
                onClick={() => openConfirm('carrito')}
                title="Vaciar carrito"
              >
                <i className={`bi bi-trash-fill ${styles.btnIconColor}`}></i>
              </Button>
            )}
          </div>
        </div>

        {items.length === 0 ? (
          <div className={styles.vacioContenedor}>
            <ComponenteError tipo="I" idAviso={4} />
          </div>
        ) : (
          <div className={styles.checkoutGrid}>
            <div className={styles.itemsColumn}>
              {itemsSeparados.map((grupo, indexGrupo) => (
                <div key={indexGrupo} className={styles.grupoCarrito}>
                  <div className={styles.grupoTitulo}>
                    <div className={styles.vendedorContenedor}>
                      <span className={styles.vendedorLabel}>Vendedor</span>
                      <button
                        className={styles.buttonPublicaciones}
                        title="Ver más publicaciones de este proveedor"
                        onClick={() => irAPublicacionesFiltrandoPorVendedor(grupo)}
                      >
                        {grupo[0].razonSocial}
                      </button>
                    </div>
                    <Precios
                      nombre={'Subtotal'}
                      items={grupo}
                      showSinIVA={true}
                      monedaSimbolo={'$'}
                      className={styles.subtotalGrupo}
                    />
                  </div>
                  <div className={styles.grupoItems}>
                    {grupo.map((item) => (
                      <CarritoItem
                        key={item.idPublicacion}
                        item={item}
                        onDelete={handleConfirmarBorradoItem}
                        sumar={sumar}
                        totalSuma={totalSuma}
                        sumarTotales={sumarTotales}
                        centroDeCostos={centroDeCostos}
                        modificarCarrito={modificarCarrito}
                        copiar={copiar}
                        setItems={setItems}
                        items={items}
                        adjunto={adjuntos.find((a) => a.idPublicacion === item.idPublicacion)}
                        loadingImagenes={loadingImagenes}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.summaryColumn}>
              <div className={styles.summarySticky}>
                <div className={styles.summaryCard}>
                  <h2 className={styles.sectionTitle}>Resumen de compra</h2>
                  <div className={styles.resumenTotal}>
                    <Precios
                      nombre={'Total'}
                      items={itemsSeparados.flat()}
                      showSinIVA={true}
                      monedaSimbolo={'$'}
                    />
                  </div>

                  <div className={styles.resumenAccion}>
                    {hayItemsSinStock ? (
                      <p className={styles.sinStock}>Sin Stock</p>
                    ) : (
                      <div className={styles.botonContinuarWrapper}>
                        <button
                          className="btn-primary btnAccionCompra"
                          type="button"
                          disabled={
                            !hayItemsComprables ||
                            (!aceptaNoHayDatosFiscales && !existeDatosFiscales)
                          }
                          onClick={() => navigate('/Compras')}
                        >
                          Continuar compra
                        </button>
                        {!aceptaNoHayDatosFiscales && !existeDatosFiscales && (
                          <span className={styles.tooltipDatosFiscales}>
                            Complete sus datos fiscales para continuar
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <GenericModal onClose={handleCloseConfirm} onConfirm={confirmarBorrado} />
    </>
  )
}

export default Carrito
